/** Max characters sent to the beautify endpoint (must stay within server limit). */
export const BEAUTIFY_SELECTION_MAX_CHARS = 48_000;

export type BeautifySelectionResult =
  | { ok: true; suggestedText: string }
  | { ok: false; error: string };

function apiBaseUrl(): string {
  const v = import.meta.env.VITE_BEAUTIFY_SELECTION_API as string | undefined;
  if (v && v.trim()) return v.replace(/\/$/, "");
  return "";
}

export function beautifySelectionEndpoint(): string {
  const base = apiBaseUrl();
  return base ? `${base}/api/beautify-selection` : "/api/beautify-selection";
}

/** Parse API response body; surfaces HTML/empty proxy failures as actionable messages. */
export function parseBeautifyHttpBody(raw: string, status: number): { ok: true; data: unknown } | { ok: false; error: string } {
  const trimmed = raw.trim();
  if (!trimmed) {
    return {
      ok: false,
      error: `Empty response (HTTP ${status}). Start the backend: GEMINI_API_KEY=… npm run beautify-server (listens on port 3847 by default). With Vite, use npm run dev or npm run preview so /api/beautify-selection is proxied to that port.`,
    };
  }
  try {
    return { ok: true, data: JSON.parse(raw) as unknown };
  } catch {
    const isHtml = /^\s*</.test(trimmed) && /<\/?(html|head|body|!DOCTYPE)/i.test(trimmed.slice(0, 500));
    if (isHtml) {
      return {
        ok: false,
        error: `Beautify API returned a web page (HTTP ${status}), not JSON. Open the app with Vite (npm run dev / npm run preview) while beautify-server is running, or set VITE_BEAUTIFY_SELECTION_API at build time to your real API base URL.`,
      };
    }
    const snippet = trimmed.length > 160 ? `${trimmed.slice(0, 160)}…` : trimmed;
    return { ok: false, error: `Server did not return JSON (HTTP ${status}): ${snippet}` };
  }
}

export async function requestBeautifySelection(text: string): Promise<BeautifySelectionResult> {
  if (!text.length) return { ok: false, error: "Nothing to send." };
  if (text.length > BEAUTIFY_SELECTION_MAX_CHARS) {
    return { ok: false, error: `Selection exceeds ${BEAUTIFY_SELECTION_MAX_CHARS} characters.` };
  }
  try {
    const res = await fetch(beautifySelectionEndpoint(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text }),
    });
    const raw = await res.text();
    const parsed = parseBeautifyHttpBody(raw, res.status);
    if (!parsed.ok) return { ok: false, error: parsed.error };
    const data = parsed.data;
    if (!res.ok) {
      const err =
        typeof (data as { error?: string })?.error === "string"
          ? (data as { error: string }).error
          : `HTTP ${res.status}`;
      return { ok: false, error: err };
    }
    const suggested = (data as { suggestedText?: string }).suggestedText;
    if (typeof suggested !== "string" || !suggested.length) {
      return { ok: false, error: "Server returned no suggestion." };
    }
    return { ok: true, suggestedText: suggested };
  } catch (e) {
    return {
      ok: false,
      error:
        e instanceof Error
          ? e.message
          : "Network error (is the beautify server running and Vite proxy configured?)",
    };
  }
}
